import React, { forwardRef } from 'react';
import { useMediaQuery, Theme, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MuiBreadcrumbs, { BreadcrumbsProps as MuiBreadcrumbsProps } from '@mui/material/Breadcrumbs';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import { Typography } from '../../data-display/typography';
import { Link } from '../link';

export type LinkItem = {
  to?: string;
  label?: string | React.ReactElement | null;
  key?: string;
};

const linkTruncationStyling = {
  maxWidth: 400,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export type BreadcrumbsProps = MuiBreadcrumbsProps & { links: LinkItem[] };
export const Breadcrumbs = forwardRef<HTMLDivElement, BreadcrumbsProps>(
  ({ links, ...rest }, ref) => {
    const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    const renderLinks = () => {
      const linksToRender = links
        .filter((link) => link.label)
        .map((link) => {
          const key = `${link.to}-${link.label}-${link.key}`;
          if (link.to) {
            return (
              <Link
                key={key}
                component={RouterLink}
                to={link.to || ''}
                color='inherit'
                display='inline-block'
                sx={{ opacity: 0.7, '&:hover': { opacity: 1 }, ...linkTruncationStyling }}
              >
                {link.label}
              </Link>
            );
          } else {
            return (
              <Typography
                key={key}
                component='span'
                variant='subtitle1'
                display='inline-block'
                sx={linkTruncationStyling}
              >
                {link.label}
              </Typography>
            );
          }
        });

      return isMobileView && linksToRender.length > 1 ? (
        <Box sx={{ flexDirection: 'row', alignSelf: 'center' }}>
          <KeyboardArrowLeftIcon fontSize='small' />
          {linksToRender[linksToRender.length - 2]}
        </Box>
      ) : (
        linksToRender
      );
    };

    return (
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize='small' sx={{ opacity: 0.7 }} />}
        sx={{ color: 'inherit' }}
        aria-label='breadcrumb'
        {...rest}
        ref={ref}
      >
        {renderLinks()}
      </MuiBreadcrumbs>
    );
  },
);
